import { useCallback, useRef, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { TpDashboardDefinition } from '../dashboard/interfaces';
import { useLoadViewState, ViewStateLoadStatus } from './useLoadViewState';
import { useSearchParams } from 'react-router-dom';
import { useApiContext } from '../../../api';
import { ViewStateMetadata, ViewStateUpdateRequest } from '../../../api/models/apis';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { selectDashboardDefs } from '../data/dashboardsSlice';
import { TpDataContext } from '../../../data/interfaces';
import {
  buildDashboardStatePayload,
  DASHBOARD_STATE_PAYLOAD_VERSION,
  VIEW_STATE_TERMS,
  VIEW_STATE_QUERY_PARAM,
} from '../../../data/snapshot/dashboardStateSerialization';
import { compressJson } from '../../../utils/compression/compressionWorkerClient';

export type TpViewStateManagerProps = {
  assetInfo: TpAssetInfo;
  dataContext: TpDataContext;
};

function sortViewStatesByCreationDate(viewStates: ViewStateMetadata[]): ViewStateMetadata[] {
  return [...viewStates].sort((left, right) => {
    const dateDifference = new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    return dateDifference || left.id.localeCompare(right.id);
  });
}

export function useViewStateManager(assetInfo: TpAssetInfo, dataContext: TpDataContext) {
  const api = useApiContext();
  const { loadViewState } = useLoadViewState();

  // Fetch dashboardDefinition from redux
  const dashboardDefs = useAppSelector(selectDashboardDefs).dashboardDefs;
  const dashboardDef: TpDashboardDefinition | undefined = dashboardDefs[assetInfo.id];
  const currentTemplateId = dashboardDef?.templateInfo.id ?? '';

  const [searchParams, setSearchParams] = useSearchParams();
  const [viewStates, setViewStates] = useState<ViewStateMetadata[]>([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const activeViewStateId = searchParams.get(VIEW_STATE_QUERY_PARAM);

  // Tracks the ID of the viewstate currently active in memory to avoid duplicate fetches
  const appliedViewStateIdRef = useRef<string | null>(null);
  const hasFetchedRef = useRef(false);
  const listRequestIdRef = useRef(0);

  // Updates the URL search parameters to reflect the currently active view state.
  const setViewStateQuery = (stateId: string | null) => {
    setSearchParams(
      (prevParams) => {
        const nextParams = new URLSearchParams(prevParams);
        if (stateId) {
          nextParams.set(VIEW_STATE_QUERY_PARAM, stateId);
        } else {
          nextParams.delete(VIEW_STATE_QUERY_PARAM);
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  const fetchViewStateList = useCallback(
    async (force = false) => {
      // If already fetched and not forced, skip extra network calls
      if (hasFetchedRef.current && !force) return;

      const requestId = ++listRequestIdRef.current;
      setIsListLoading(true);
      setListError(null);
      try {
        const result = await api.viewState.listViewStates(assetInfo.id);
        if (requestId !== listRequestIdRef.current) return result;
        setViewStates(sortViewStatesByCreationDate(result));
        hasFetchedRef.current = true;
        return result;
      } catch (err: any) {
        if (requestId === listRequestIdRef.current) {
          const message = err.message || `Failed to fetch ${VIEW_STATE_TERMS.plural}`;
          setListError(message);
          throw new Error(message);
        }
      } finally {
        if (requestId === listRequestIdRef.current) setIsListLoading(false);
      }
    },
    [api, assetInfo.id],
  );

  // Save ViewState
  const saveViewState = async (name: string, description?: string | null, visibility = 'private') => {
    const payload = buildDashboardStatePayload(dataContext, assetInfo, dashboardDef);
    const response = await api.viewState.createViewState(assetInfo.id, {
      name,
      description,
      visibility,
      dataContextId: dataContext.id,
      schemaVersion: DASHBOARD_STATE_PAYLOAD_VERSION,
      compressedState: await compressJson(payload),
    });

    appliedViewStateIdRef.current = response.id;
    setViewStateQuery(response.id);
    setViewStates((current) =>
      sortViewStatesByCreationDate([response, ...current.filter((item) => item.id !== response.id)]),
    );
    return response.id;
  };

  // Select a ViewState by updating the URL; the URL-driven load path applies it.
  const selectViewState = (stateId: string) => {
    appliedViewStateIdRef.current = stateId;
    setViewStateQuery(stateId);
  };

  // Load a viewstate from server and select it upon success
  const loadViewStateFromServer = async (stateId: string) => {
    const loadStatus: ViewStateLoadStatus = await loadViewState({ stateId, assetInfo, dataContext, currentTemplateId });
    if (loadStatus === 'success') {
      selectViewState(stateId);
    }
    return loadStatus;
  };

  const updateViewState = async (stateId: string, updates: ViewStateUpdateRequest) => {
    const updated = await api.viewState.updateViewState(stateId, updates);
    setViewStates((current) =>
      sortViewStatesByCreationDate(current.map((item) => (item.id === stateId ? updated : item))),
    );
  };

  const deleteViewState = async (stateId: string) => {
    await api.viewState.deleteViewState(stateId);
    setViewStates((current) => sortViewStatesByCreationDate(current.filter((item) => item.id !== stateId)));
    if (stateId === activeViewStateId) {
      appliedViewStateIdRef.current = null;
      setViewStateQuery(null);
    }
  };

  const clearActiveViewState = () => {
    appliedViewStateIdRef.current = null;
    setViewStateQuery(null);
  };

  return {
    activeViewStateId,
    viewStates,
    isListLoading,
    listError,
    fetchViewStateList,
    saveViewState,
    loadViewStateFromServer,
    selectViewState,
    updateViewState,
    deleteViewState,
    clearActiveViewState,
  };
}
